import { useEffect, useRef, useState, type ReactNode } from "react";
import { getJson, postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  FilterRow,
  NoResults,
  Pill,
  RadioCards,
  RowMenu,
  RowsSkeleton,
  SearchBox,
  Window,
  useFilters,
  useIsMobile,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, TextField, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import { type OrgHead } from "./employees";
import { BlockFailed, Failed, replyMessage } from "./EmployeesPages";
import {
  byDay,
  conversationFilters,
  conversationHref,
  conversationTabs,
  eventText,
  eventWho,
  kindText,
  timeIn,
  type ConversationEvent,
  type ConversationRow,
  type ConversationTab,
} from "./messaging";
import { MessagingHead } from "./MessagingPages";
import { dateIn, initials, plural, personHue } from "./organizations";
import { ConfirmWindow } from "./PersonActions";
import { SimpleTable } from "./SimpleTable";
import own from "./messaging.module.css";

// R6 · Empresa › Mensajería › Conversaciones internas y su ficha (§5.7). El Platform Admin administra los grupos (renombrar,
// cerrar, quitar a alguien, quién puede crear grupos) pero no escribe en ellos: la conversación es de solo lectura (C41).

const policyKeys: Record<string, { short: string; title: string; line: string }> = {
  ALL_MESSAGING_USERS: {
    short: "ADM_MSG_POLICY_ALL_SHORT",
    title: "ADM_MSG_POLICY_ALL",
    line: "ADM_MSG_POLICY_ALL_LINE",
  },
  ADMINS_ONLY: {
    short: "ADM_MSG_POLICY_ADMINS_SHORT",
    title: "ADM_MSG_POLICY_ADMINS",
    line: "ADM_MSG_POLICY_ADMINS_LINE",
  },
};

/** Aviso de una línea con su ícono (candado de C41, personas) y una acción opcional a la derecha. */
export function LineNotice({ icon, children, action }: { icon: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={own.notice} role="status" data-part="aviso">
      <ChatIcon name={icon} />
      <span>{children}</span>
      {action}
    </div>
  );
}

/** Estado de una conversación (Abierta · Cerrada) como texto de la fila. */
const statusText = (L: (key: string) => string, open: boolean) => L(open ? "ORGCHAT_OPEN" : "ORGCHAT_CLOSED");

/**
 * `emp-conversaciones` · aviso de quién crea grupos con «Cambiar» (ventana `grupos`), buscador y píldoras Todas ·
 * Abiertas · Cerradas en la URL, tabla Conversación · Tipo · Participantes · Estado paginada en Messaging (B3). Clic en
 * la fila abre la ficha. Desde aquí no se escribe.
 */
export function ConversationsPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const filters = useFilters();
  const search = filters.get("search");
  const status = (conversationFilters as readonly string[]).includes(filters.get("status"))
    ? filters.get("status")
    : "ALL";
  const query = new URLSearchParams();
  if (search) query.set("search", search);
  if (status !== "ALL") query.set("status", status);
  const list = useServerList<ConversationRow>(
    `/api/admin/organizations/${orgId}/internal-conversations${query.size ? `?${query}` : ""}`,
  );
  const [changing, setChanging] = useState(false);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  const policy = (list.extra?.groupPolicy as string | null | undefined) ?? null;
  useOpenOrg(org);
  useTitle(`${L("ADM_ORG_MESSAGING")} · ${L("ADM_MSG_TAB_CONVERSATIONS")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const head = <MessagingHead orgId={orgId} current="conversations" sub={L("ADM_MSG_CONVERSATIONS_LINE")} />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  if (list.state === "error" && !list.extra) return <Failed head={head} status={undefined} retry={list.reload} />;
  const known = policy ? policyKeys[policy] : undefined;
  const savePolicy = async (next: string) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/group-policy`,
      { policy: next },
    );
    if (reply.status === 200 && reply.body?.saved) {
      list.reload();
      toast.show(L(next === "ADMINS_ONLY" ? "ADM_MSG_POLICY_SAVED_ADMINS" : "ADM_MSG_POLICY_SAVED_ALL"));
      return null;
    }
    return replyMessage(L, reply.body);
  };
  const filtered = !!search || status !== "ALL";
  return (
    <>
      {head}
      {list.state === "loading" && !list.extra ? (
        <div className={own.notice} aria-hidden="true">
          <span className={ui.bone} style={{ display: "block", width: 280, height: 14 }} />
        </div>
      ) : (
        <LineNotice
          icon="users"
          action={
            <button type="button" className={own.noticeAction} onClick={() => setChanging(true)}>
              {L("ADM_AI_CHANGE")}
            </button>
          }
        >
          {formatText(L("ADM_MSG_POLICY_NOTICE"), known ? L(known.short) : "—")}
        </LineNotice>
      )}
      <div className={own.filters}>
        <SearchBox
          id="conversations-search"
          value={search}
          placeholder={L("ADM_MSG_SEARCH_CONVERSATION")}
          onChange={(value) => filters.set({ search: value || null })}
        />
        <FilterRow label={L("ADM_MSG_FILTERS")}>
          {conversationFilters.map((value) => (
            <Pill
              key={value}
              pressed={status === value}
              onClick={() => filters.set({ status: value === "ALL" ? null : value })}
            >
              {L(value === "ALL" ? "ADM_AI_ALL_F" : value === "OPEN" ? "ADM_MSG_OPEN_PLURAL" : "ADM_MSG_CLOSED_PLURAL")}
            </Pill>
          ))}
        </FilterRow>
      </div>
      {list.state === "loading" ? (
        <>
          <Counter>
            <span className={ui.bone} style={{ display: "inline-block", width: 100, height: 12 }} />
          </Counter>
          <RowsSkeleton rows={4} table={!mobile} />
        </>
      ) : list.state === "error" ? (
        <BlockFailed retry={list.reload} />
      ) : list.items.length === 0 && list.total > 0 ? (
        <PastEnd onReset={() => filters.set({})} />
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults
            query={search}
            things={L("ADM_MSG_THINGS_CONVERSATIONS")}
            onClear={() => filters.set({ search: null, status: null })}
          />
        ) : (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_MSG_CONVERSATIONS_EMPTY")}</strong>
            <span>{L("ADM_MSG_CONVERSATIONS_EMPTY_LINE")}</span>
            <button type="button" className="tm-btn secondary" onClick={() => setChanging(true)}>
              {L("ADM_MSG_POLICY_TITLE")}
            </button>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_MSG_N_CONVERSATIONS_ONE", "ADM_MSG_N_CONVERSATIONS")}</Counter>
          <div className={own.fit}>
            <DataTable
              label={L("ADM_MSG_TAB_CONVERSATIONS")}
              rows={list.items}
              rowKey={(row) => row.id}
              href={(row) => conversationHref(orgId, row.id)}
              columns={[
                { key: "title", title: L("ADM_MSG_COL_CONVERSATION"), render: (row) => <strong>{row.title}</strong> },
                { key: "kind", title: L("ADM_AI_TYPE"), render: (row) => kindText(L, row.kind) },
                {
                  key: "participants",
                  title: L("ADM_MSG_COL_PARTICIPANTS"),
                  numeric: true,
                  render: (row) => row.participants.toLocaleString(locale),
                },
                { key: "status", title: L("ADM_EMP_COL_STATUS"), render: (row) => statusText(L, row.open) },
              ]}
              mobile={(row) => ({
                title: row.title,
                line: [
                  kindText(L, row.kind),
                  plural(L, locale, row.participants, "ADM_MSG_N_PARTICIPANTS_ONE", "ADM_MSG_N_PARTICIPANTS"),
                  statusText(L, row.open),
                ].join(" · "),
              })}
            />
          </div>
          <ListFooter list={list} />
        </>
      )}
      <PolicyWindow open={changing} current={policy} onClose={() => setChanging(false)} onSave={savePolicy} />
    </>
  );
}

/** Ventana `grupos` · «Quién puede crear grupos» con dos tarjetas de opción (C08). */
function PolicyWindow({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: string | null;
  onClose: () => void;
  onSave: (policy: string) => Promise<string | null>;
}) {
  const { L } = useTexts();
  const [value, setValue] = useState("ALL_MESSAGING_USERS");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setValue(current === "ADMINS_ONLY" ? "ADMINS_ONLY" : "ALL_MESSAGING_USERS");
      setError(null);
    }
  }
  const save = async () => {
    if (value === current) {
      onClose();
      return;
    }
    setBusy(true);
    const failure = await onSave(value);
    setBusy(false);
    if (failure) setError(failure);
    else onClose();
  };
  return (
    <Window
      open={open}
      title={L("ADM_MSG_POLICY_TITLE")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void save()}
          >
            {L("ADM_AI_SAVE")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <RadioCards
          label={L("ADM_MSG_POLICY_TITLE")}
          value={value}
          options={["ALL_MESSAGING_USERS", "ADMINS_ONLY"].map((option) => ({
            value: option,
            title: L(policyKeys[option]!.title),
            line: L(policyKeys[option]!.line),
          }))}
          onChange={setValue}
        />
      </div>
    </Window>
  );
}

type ConversationRecord = {
  organization: OrgHead;
  conversation: { id: number; title: string; kind: string; open: boolean; canManage: boolean };
  participants: { id: number; employee: boolean; userId: number | null; name: string | null }[];
  messages: ConversationMessage[];
  hasMore: boolean;
  events: ConversationEvent[];
};

type ConversationMessage = {
  id: number;
  employee: boolean;
  system: boolean;
  /** Id de la persona que escribe (C20: su tono sale de aquí, como en el chat); null en el empleado IA o el sistema. */
  userId: number | null;
  author: string | null;
  text: string | null;
  at: string;
};

/** Persona (iniciales) o empleado IA (user-cog en morado, C20). */
function Who({ name, employee, userId }: { name: string | null; employee: boolean; userId?: number | null }) {
  const { L } = useTexts();
  return (
    <span className={own.who}>
      {employee ? (
        <span className={own.aiAvatar} aria-hidden="true">
          <ChatIcon name="user-cog" />
        </span>
      ) : (
        <span
          className={`${own.initials} npa-person-tone`}
          style={{ ["--person-hue" as string]: String(personHue(userId ?? name)) }}
          aria-hidden="true"
        >
          {name ? initials(name) : "?"}
        </span>
      )}
      <span title={name ?? undefined}>{name ?? L("ADM_MSG_UNKNOWN_NAME")}</span>
    </span>
  );
}

/**
 * `emp-conversacion` · `-part` · `-aud`: ← a Conversaciones internas, título con su tipo y estado, «N participantes», ⋮
 * con «Renombrar…» y «Cerrar grupo…» (solo un grupo abierto que se puede administrar). Pestañas Mensajes (solo lectura
 * con candado, C41), Participantes (⋮ «Quitar del grupo…» por fila) y Auditoría (D7: en palabras, sin JSON).
 */
export function ConversationPage({ orgId, caseId, tab }: { orgId: number; caseId: number; tab: ConversationTab }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const data = useAiData<ConversationRecord>(`/api/admin/organizations/${orgId}/internal-conversations/${caseId}`);
  const [renaming, setRenaming] = useState(false);
  const [closing, setClosing] = useState(false);
  const [removing, setRemoving] = useState<{ id: number; name: string } | null>(null);
  const [earlier, setEarlier] = useState<{ messages: ConversationMessage[]; hasMore: boolean } | null>(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  useOpenOrg(data.data?.organization ?? null);
  const record = data.data;
  useTitle(record?.conversation.title ?? L("ADM_MSG_TAB_CONVERSATIONS"));
  const back = { href: `/Organizations/Conversaciones?id=${orgId}`, label: L("ADM_MSG_TAB_CONVERSATIONS") };
  const tabs = (
    <nav
      className={`tm-tabs ${ui.tabs}`}
      aria-label={record?.conversation.title ?? L("ADM_MSG_TAB_CONVERSATIONS")}
      data-part="pestanas"
    >
      {conversationTabs.map((item) => (
        <a key={item} href={conversationHref(orgId, caseId, item)} aria-current={item === tab ? "page" : undefined}>
          {L(
            item === "mensajes"
              ? "ADM_MSG_TAB_MESSAGES"
              : item === "participantes"
                ? "ADM_MSG_TAB_PARTICIPANTS"
                : "ADM_MSG_TAB_AUDIT",
          )}
        </a>
      ))}
    </nav>
  );
  if (data.state === "missing")
    return (
      <PageState
        head={<PageHead title={L("ADM_MSG_TAB_CONVERSATIONS")} back={back} />}
        title={L("ADM_MSG_CONVERSATION_MISSING")}
        line={L("ADM_MSG_CONVERSATION_MISSING_LINE")}
        action={
          <a className="tm-btn secondary" href={back.href}>
            {L("ADM_MSG_BACK_CONVERSATIONS")}
          </a>
        }
      />
    );
  if (!record) {
    const head = <PageHead title={L("ADM_MSG_TAB_CONVERSATIONS")} back={back} />;
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        <PageHead
          title=""
          back={back}
          sub={<span className={ui.bone} style={{ display: "inline-block", width: 120, height: 12 }} />}
        />
        {tabs}
        <RowsSkeleton rows={4} table={!mobile && tab !== "mensajes"} />
      </>
    );
  }
  const conversation = record.conversation;
  const isGroup = conversation.kind === "GROUP";
  const manage = isGroup && conversation.open && conversation.canManage;
  const menu = manage
    ? [
        { label: L("ADM_MSG_RENAME_MENU"), onSelect: () => setRenaming(true) },
        { label: L("ADM_MSG_CLOSE_MENU"), danger: true, onSelect: () => setClosing(true) },
      ]
    : [];
  const send = async (url: string, body: unknown) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(url, body);
    if (reply.status === 200 && reply.body?.saved) return null;
    return replyMessage(L, reply.body);
  };
  const base = `/api/admin/organizations/${orgId}/internal-conversations/${caseId}`;
  const zone = record.organization.timezone;
  const messages = [...(earlier?.messages ?? []), ...record.messages];
  const hasMore = earlier ? earlier.hasMore : record.hasMore;
  const loadEarlier = async () => {
    const first = messages[0];
    if (!first) return;
    setLoadingEarlier(true);
    const reply = await getJson<{ messages: ConversationMessage[]; hasMore: boolean }>(`${base}?before=${first.id}`);
    setLoadingEarlier(false);
    if (reply.status === 200 && reply.body)
      setEarlier({ messages: [...reply.body.messages, ...(earlier?.messages ?? [])], hasMore: reply.body.hasMore });
    else toast.show(L("ADM_UNAVAILABLE_LINE"));
  };
  let body: ReactNode;
  if (tab === "mensajes") {
    body = (
      <>
        <LineNotice icon="lock">{L("ADM_MSG_READONLY_GROUPS")}</LineNotice>
        <div className={own.chat}>
          <div
            ref={(element) => {
              // Lo último a la vista al abrir la conversación (no al sumar mensajes anteriores arriba).
              if (element && !earlier && element.dataset.scrolled !== String(record.messages.length)) {
                element.scrollTop = element.scrollHeight;
                element.dataset.scrolled = String(record.messages.length);
              }
            }}
            className={own.transcript}
            role="log"
            aria-label={L("ADM_MSG_TAB_MESSAGES")}
            tabIndex={0}
          >
            {hasMore ? (
              <button
                type="button"
                className={own.earlier}
                disabled={loadingEarlier}
                aria-busy={loadingEarlier || undefined}
                onClick={() => void loadEarlier()}
              >
                {L("ADM_MSG_EARLIER")}
              </button>
            ) : null}
            {messages.length === 0 ? (
              <div className={own.chatEmpty}>
                <strong>{L("ADM_MSG_NO_MESSAGES")}</strong>
                <span>{L("ADM_MSG_NO_MESSAGES_LINE")}</span>
              </div>
            ) : (
              byDay(L, locale, messages, zone).map((group) => (
                <DayGroup key={`${group.day}-${group.items[0]!.id}`} day={group.day}>
                  {group.items.map((message) =>
                    message.system ? (
                      <p key={message.id} className={own.system}>
                        {message.text}
                      </p>
                    ) : (
                      <div key={message.id} className={own.bubble}>
                        {/* C19 1.14.8 · AR21: el nombre de quien escribe arriba de cada burbuja, en su tono. */}
                        <span
                          className={`${own.author}${message.employee ? ` ${own.authorAi}` : " npa-person-name"}`}
                          style={
                            message.employee
                              ? undefined
                              : { ["--person-hue" as string]: String(personHue(message.userId ?? message.author)) }
                          }
                        >
                          {message.author ?? L("ADM_MSG_UNKNOWN_NAME")}
                        </span>
                        <span className={own.text}>{message.text ?? "—"}</span>
                        <span className={own.time}>{timeIn(locale, message.at, zone)}</span>
                      </div>
                    ),
                  )}
                </DayGroup>
              ))
            )}
          </div>
        </div>
      </>
    );
  } else if (tab === "participantes") {
    const rowMenu = (participant: { id: number; name: string | null }) =>
      manage ? (
        <RowMenu
          label={formatText(L("ADM_PPL_ACTIONS_FOR"), participant.name ?? L("ADM_MSG_UNKNOWN_NAME"))}
          title={participant.name ?? undefined}
          items={[
            {
              label: L("ADM_MSG_REMOVE_MENU"),
              danger: true,
              onSelect: () => setRemoving({ id: participant.id, name: participant.name ?? L("ADM_MSG_UNKNOWN_NAME") }),
            },
          ]}
        />
      ) : null;
    body =
      record.participants.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_NO_PARTICIPANTS")}</strong>
          <a className="tm-btn secondary" href={conversationHref(orgId, caseId, "auditoria")}>
            {L("ADM_MSG_SEE_AUDIT")}
          </a>
        </div>
      ) : (
        <>
          <Counter>
            {plural(L, locale, record.participants.length, "ADM_MSG_N_PARTICIPANTS_ONE", "ADM_MSG_N_PARTICIPANTS")}
          </Counter>
          <div className={own.fit}>
            <SimpleTable
              label={L("ADM_MSG_TAB_PARTICIPANTS")}
              rows={record.participants}
              rowKey={(participant) => participant.id}
              columns={[
                {
                  key: "who",
                  title: L("ADM_MSG_COL_PARTICIPANT"),
                  render: (participant) => (
                    <Who name={participant.name} employee={participant.employee} userId={participant.userId} />
                  ),
                },
                {
                  key: "kind",
                  title: L("ADM_AI_TYPE"),
                  render: (participant) => L(participant.employee ? "ORGCHATD_AGENT" : "ORGCHATD_HUMAN"),
                },
                ...(manage ? [{ key: "menu", title: "", className: own.endCell, render: rowMenu }] : []),
              ]}
              mobile={(participant) => ({
                title: participant.name ?? L("ADM_MSG_UNKNOWN_NAME"),
                line: L(participant.employee ? "ORGCHATD_AGENT" : "ORGCHATD_HUMAN"),
                end: rowMenu(participant),
              })}
            />
          </div>
        </>
      );
  } else {
    body =
      record.events.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_MSG_NO_EVENTS")}</strong>
          <a className="tm-btn secondary" href={conversationHref(orgId, caseId, "mensajes")}>
            {L("ADM_MSG_SEE_MESSAGES")}
          </a>
        </div>
      ) : (
        <div className={own.audit}>
          <SimpleTable
            label={L("ADM_MSG_TAB_AUDIT")}
            rows={record.events}
            rowKey={(event) => event.id}
            columns={[
              { key: "at", title: L("ADM_MSG_COL_DATE"), render: (event) => dateIn(locale, event.at, zone, false) },
              { key: "what", title: L("ADM_MSG_COL_WHAT"), render: (event) => eventText(L, event) },
              { key: "who", title: L("ADM_MSG_COL_WHO"), render: (event) => eventWho(L, event) },
            ]}
            mobile={(event) => ({
              title: eventText(L, event),
              line: `${dateIn(locale, event.at, zone, false)} · ${eventWho(L, event)}`,
            })}
          />
        </div>
      );
  }
  return (
    <div className={own.wrapHead}>
      <PageHead
        title={conversation.title}
        back={back}
        titleTag={
          <>
            <span className="tm-tag">{kindText(L, conversation.kind)}</span>
            <span className="tm-tag">{statusText(L, conversation.open)}</span>
          </>
        }
        sub={plural(L, locale, record.participants.length, "ADM_MSG_N_PARTICIPANTS_ONE", "ADM_MSG_N_PARTICIPANTS")}
      >
        {menu.length ? <RowMenu label={L("ADM_MORE_ACTIONS")} title={conversation.title} items={menu} /> : null}
      </PageHead>
      {tabs}
      {body}
      <RenameWindow
        open={renaming}
        current={conversation.title}
        onClose={() => setRenaming(false)}
        onSave={async (title) => {
          const failure = await send(`${base}/rename`, { title });
          if (!failure) {
            toast.show(L("ADM_MSG_RENAMED"));
            setEarlier(null);
            void data.reload();
          }
          return failure;
        }}
      />
      <ConfirmWindow
        open={closing}
        title={L("ADM_MSG_CLOSE_TITLE")}
        line={formatText(L("ADM_MSG_CLOSE_LINE"), conversation.title)}
        action={L("ORGCHATD_CLOSE")}
        onClose={() => setClosing(false)}
        onConfirm={async () => {
          const failure = await send(`${base}/close`, {});
          toast.show(failure ?? L("ADM_MSG_CLOSED_TOAST"));
          setEarlier(null);
          void data.reload();
          return true;
        }}
      />
      <ConfirmWindow
        open={!!removing}
        title={formatText(L("ADM_MSG_REMOVE_TITLE"), removing?.name ?? "")}
        line={L("ADM_MSG_REMOVE_LINE")}
        action={L("ADM_EMP_REMOVE")}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return true;
          const failure = await send(`${base}/participants/${removing.id}/remove`, {});
          toast.show(failure ?? formatText(L("ADM_MSG_REMOVED_TOAST"), removing.name));
          setEarlier(null);
          void data.reload();
          return true;
        }}
      />
    </div>
  );
}

/**
 * Una página más allá del final (un enlace viejo, o filas que cambiaron en otra pestaña): vuelve sola a la primera, nunca
 * dice «vacío» con un total mayor que 0 (H-3).
 */
export function PastEnd({ onReset }: { onReset: () => void }) {
  const reset = useRef(onReset);
  useEffect(() => {
    reset.current();
  }, []);
  return <RowsSkeleton rows={3} />;
}

/** Grupo de mensajes de un día con su separador (C19). */
export function DayGroup({ day, children }: { day: string; children: ReactNode }) {
  return (
    <>
      <span className={own.day}>{day}</span>
      {children}
    </>
  );
}

/** Ventana `renombrar` · «Renombrar grupo» con el nombre actual; el error se borra al escribir uno válido (E81). */
function RenameWindow({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: string;
  onClose: () => void;
  onSave: (title: string) => Promise<string | null>;
}) {
  const { L } = useTexts();
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  if (open !== shown) {
    setShown(open);
    if (open) {
      setTitle(current);
      setError(null);
    }
  }
  const save = async () => {
    const value = title.trim();
    if (!value) {
      setError(L("ADM_MSG_ERR_GROUP_NAME"));
      requestAnimationFrame(() => document.getElementById("group-name")?.focus());
      return;
    }
    if (value === current) {
      onClose();
      return;
    }
    setBusy(true);
    const failure = await onSave(value);
    setBusy(false);
    if (failure) setError(failure);
    else onClose();
  };
  return (
    <Window
      open={open}
      title={L("ADM_MSG_RENAME_TITLE")}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void save()}
          >
            {L("ADM_AI_SAVE")}
          </button>
        </>
      }
    >
      <TextField
        id="group-name"
        label={L("ADM_AI_NAME")}
        value={title}
        error={error}
        maxLength={200}
        onChange={(value) => {
          setTitle(value);
          if (value.trim()) setError(null);
        }}
      />
    </Window>
  );
}
